import type { BootstrapConfig } from '../api/types';

/**
 * Strongly typed builder configuration.
 * Parsed from the BootstrapConfig settings with sensible defaults.
 */
export interface ThemeColors {
    /** Primary accent — buttons, active step indicator underline */
    primary: string;
    /** Primary hover state */
    primaryHover: string;
    /** Light primary tint — selected swatch rings, highlights */
    primaryLight: string;
    /** Border / step-nav bottom line */
    border: string;
    /** Main body text */
    text: string;
    /** Widget / page background */
    bg: string;
}

export interface BuilderSettings {
    /** Enable Collection (model browser) step. -1 = disabled */
    enableCollectionStep: number;
    /** Enable seat / upholstery color step. -1 = disabled */
    enableSeatColorStep: number;
    /** Enable the dealer portal login */
    enableDealerPortal: boolean;
    /** Show sale price column */
    showSalePrice: boolean;
    /** Show dealer-net column (only when dealer is logged in) */
    showDealerNet: boolean;
    /** Exchange rate multiplier (1.0 = no conversion) */
    exchangeRate: number;
    /** Currency code for formatting (e.g., 'USD', 'CAD') */
    currency: string;
    /** Locale for number/currency formatting */
    locale: string;
    /** Company disclaimer text for quotes/PDFs */
    disclaimer: string;
    /** Default PDF layout ('default' | 'wide') */
    pdfLayout: string;
    /** Message shown to the visitor after a successful quote submission */
    quoteThankYouMessage: string;
    /** Skip straight to a specific model ID (bypasses collection) */
    directModelId: number | null;
    /** Per-tenant theme colors — applied as CSS custom properties */
    theme: ThemeColors;
}

/**
 * Parse the raw API settings into a strongly typed object with defaults.
 */
export function parseSettings(config: BootstrapConfig): BuilderSettings {
    const s = (config.settings ?? {}) as Record<string, unknown>;

    const hex = (key: string, fallback: string): string =>
        typeof s[key] === 'string' && /^#[0-9a-fA-F]{6}$/.test(s[key] as string)
            ? (s[key] as string)
            : fallback;

    return {
        enableCollectionStep: typeof s.enableCollectionStep === 'number' ? s.enableCollectionStep : 0,
        enableSeatColorStep: typeof s.enableSeatColorStep === 'number' ? s.enableSeatColorStep : -1,
        enableDealerPortal: s.enableDealerPortal === true,
        showSalePrice: s.showSalePrice !== false,
        showDealerNet: s.showDealerNet === true,
        exchangeRate: typeof s.exchangeRate === 'number' ? s.exchangeRate : 1.0,
        currency: typeof s.currency === 'string' ? s.currency : 'USD',
        locale: typeof s.locale === 'string' ? s.locale : 'en-US',
        disclaimer: typeof s.disclaimer === 'string' ? s.disclaimer : '',
        pdfLayout: typeof s.pdfLayout === 'string' ? s.pdfLayout : 'default',
        quoteThankYouMessage: typeof s.quoteThankYouMessage === 'string' ? s.quoteThankYouMessage : '',
        directModelId: typeof s.directModelId === 'number' ? s.directModelId : null,
        theme: {
            primary:      hex('themeColorPrimary',      '#0e8e92'),
            primaryHover: hex('themeColorPrimaryHover', '#1557b0'),
            primaryLight: hex('themeColorPrimaryLight', '#e8f0fe'),
            border:       hex('themeColorBorder',       '#0e8e92'),
            text:         hex('themeColorText',         '#202124'),
            bg:           hex('themeColorBg',           '#ffffff'),
        },
    };
}
